import { useEffect, useState, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// PWA
import PWAInstallPrompt, { registerServiceWorker } from "./components/PWAInstallPrompt";

// Guards
import SubscriptionGuard from "./components/SubscriptionGuard";

// Core pages (loaded immediately)
import HomePage from "./pages/HomePage";
import SearchResultsPage from "./pages/SearchResultsPage";
import PharmacyDetailsPage from "./pages/PharmacyDetailsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ClientRegisterPage from "./pages/ClientRegisterPage";
import MessagesPage from "./pages/MessagesPage";
import PurchasesPage from "./pages/PurchasesPage";
import LegalPage from "./pages/LegalPage";
import PrivacyPage from "./pages/PrivacyPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import DoctorRegisterPage from "./pages/DoctorRegisterPage";
import DoctorPrescriptionsPage from "./pages/doctor/PrescriptionsPage";
import DeliveryDashboard from "./pages/delivery/Dashboard";

// Pharmacy pages (loaded immediately - main user flow)
import PharmacyDashboard from "./pages/pharmacy/Dashboard";
import PharmacyInventory from "./pages/pharmacy/Inventory";
import PharmacyProfile from "./pages/pharmacy/Profile";
import PharmacySubscription from "./pages/pharmacy/Subscription";
import PharmacySellers from "./pages/pharmacy/Sellers";
import PharmacyMarketData from "./pages/pharmacy/MarketData";
import PharmacyExpiringProducts from "./pages/pharmacy/ExpiringProducts";
import PharmacyStockValue from "./pages/pharmacy/StockValue";
import PharmacyBusinessAccount from "./pages/pharmacy/BusinessAccount";

// Admin pages (lazy loaded - only for admins)
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminPharmacies = lazy(() => import("./pages/admin/Pharmacies"));
const AdminMedicines = lazy(() => import("./pages/admin/Medicines"));
const AdminMedicineForms = lazy(() => import("./pages/admin/MedicineCatalogAdmin").then((m) => ({ default: m.AdminMedicineForms })));
const AdminMedicineCategories = lazy(() => import("./pages/admin/MedicineCatalogAdmin").then((m) => ({ default: m.AdminMedicineCategories })));
const AdminPlans = lazy(() => import("./pages/admin/Plans"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminChangePassword = lazy(() => import("./pages/admin/ChangePassword"));
const AdminPaymentConfig = lazy(() => import("./pages/admin/PaymentConfig"));
const AdminClients = lazy(() => import("./pages/admin/Clients"));
const AdminSearchAnalytics = lazy(() => import("./pages/admin/SearchAnalytics"));
const AdminMedicinePublicHealthStats = lazy(() => import("./pages/admin/MedicinePublicHealthStats"));
const AdminMyPharmacies = lazy(() => import("./pages/admin/MyPharmacies"));
const AdminSubscriptionManagement = lazy(() => import("./pages/admin/SubscriptionManagement"));
const AdminSubscriptionStats = lazy(() => import("./pages/admin/SubscriptionStats"));
const AdminSubscriptionPharmacies = lazy(() => import("./pages/admin/SubscriptionPharmacies"));
const AdminPaymentHistory = lazy(() => import("./pages/admin/PaymentHistory"));
const AdminDeliveryPricing = lazy(() => import("./pages/admin/DeliveryPricing"));
const AdminDeliveries = lazy(() => import("./pages/admin/Deliveries"));
const AdminRoles = lazy(() => import("./pages/admin/AdminRoles"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminDoctors = lazy(() => import("./pages/admin/Doctors"));
const AdminKamUsers = lazy(() => import("./pages/admin/KamUsers"));
const AdminKamCommissions = lazy(() => import("./pages/admin/KamCommissions"));
const AdminDoctorCommissionConfig = lazy(() => import("./pages/admin/DoctorCommissionConfig"));
const AdminDoctorCommissions = lazy(() => import("./pages/admin/DoctorCommissions"));
const AdminConfigurationIndex = lazy(() => import("./pages/admin/ConfigurationIndex"));
const AdminKamCommissionSettings = lazy(() => import("./pages/admin/KamCommissionSettings"));
import KamDashboard from "./pages/kam/Dashboard";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SellerDashboard from "./pages/seller/Dashboard";
import SellerPOS from "./pages/seller/POS";
import SellerSales from "./pages/seller/Sales";
import SellerInvoices from "./pages/seller/Invoices";
import GPSGuard from "./components/GPSGuard";
import apiClient from "./lib/api";
import { canAccessAdminPath, canReadModule } from "./lib/adminPermissions";
import { getDefaultConfigurationPath } from "./lib/adminNavConfig";

// Register Service Worker for PWA
registerServiceWorker();

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("omu_token"));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("omu_token");
      if (storedToken) {
        try {
          const response = await apiClient.get("/auth/me");
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem("omu_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await apiClient.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("omu_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("omu_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-omu-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function getAdminAccessFallback(user) {
  if (canAccessAdminPath(user, "/admin")) return "/admin";
  const configPath = getDefaultConfigurationPath(user, canReadModule);
  if (configPath) return configPath;
  return "/admin/change-password";
}

const AdminProtectedRoute = ({ children, path }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-omu-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  if (!canAccessAdminPath(user, path)) {
    return <Navigate to={getAdminAccessFallback(user)} replace />;
  }

  return children;
};

const AdminLegacyRedirect = ({ to }) => (
  <AdminProtectedRoute path={to}>
    <Navigate to={to} replace />
  </AdminProtectedRoute>
);

// Protected Pharmacy Route with Subscription Check
const ProtectedPharmacyRoute = ({ children, requireSubscription = true }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-omu-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "pharmacy") {
    return <Navigate to="/" replace />;
  }

  // Wrap with subscription guard if subscription is required
  if (requireSubscription) {
    return <SubscriptionGuard>{children}</SubscriptionGuard>;
  }

  return children;
};

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-omu-blue-500"></div>
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      {/* Public Routes - NO GPS required */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/client" element={<ClientRegisterPage />} />
      <Route path="/register/doctor" element={<DoctorRegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/mentions-legales" element={<LegalPage />} />
      <Route path="/politique-confidentialite" element={<PrivacyPage />} />
      <Route path="/supprimer-mon-compte" element={<DeleteAccountPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      
      {/* ALL other routes require GPS */}
      <Route path="/*" element={
        <GPSGuard>
          <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/search" element={<SearchResultsPage />} />
      <Route path="/pharmacy/:id" element={<PharmacyDetailsPage />} />

      {/* Pharmacy Dashboard Routes - Protected with Subscription */}
      <Route
        path="/dashboard"
        element={
          <ProtectedPharmacyRoute>
            <PharmacyDashboard />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/inventory"
        element={
          <ProtectedPharmacyRoute>
            <PharmacyInventory />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/profile"
        element={
          <ProtectedPharmacyRoute requireSubscription={false}>
            <PharmacyProfile />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/subscription"
        element={
          <ProtectedPharmacyRoute requireSubscription={false}>
            <PharmacySubscription />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/sellers"
        element={
          <ProtectedPharmacyRoute>
            <PharmacySellers />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/market-data"
        element={
          <ProtectedPharmacyRoute>
            <PharmacyMarketData />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/expiring-products"
        element={
          <ProtectedPharmacyRoute>
            <PharmacyExpiringProducts />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/stock-value"
        element={
          <ProtectedPharmacyRoute>
            <PharmacyStockValue />
          </ProtectedPharmacyRoute>
        }
      />
      <Route
        path="/dashboard/business"
        element={
          <ProtectedPharmacyRoute>
            <PharmacyBusinessAccount />
          </ProtectedPharmacyRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute path="/admin">
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/pharmacies"
        element={
          <AdminProtectedRoute path="/admin/pharmacies">
            <AdminPharmacies />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/doctors"
        element={
          <AdminProtectedRoute path="/admin/doctors">
            <AdminDoctors />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/my-pharmacies"
        element={
          <AdminProtectedRoute path="/admin/my-pharmacies">
            <AdminMyPharmacies />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/medicines"
        element={
          <AdminProtectedRoute path="/admin/medicines">
            <AdminMedicines />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/medicine-forms"
        element={
          <AdminProtectedRoute path="/admin/medicine-forms">
            <AdminMedicineForms />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/medicine-categories"
        element={
          <AdminProtectedRoute path="/admin/medicine-categories">
            <AdminMedicineCategories />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/plans"
        element={<AdminLegacyRedirect to="/admin/configuration/monetization/plans-mm" />}
      />
      <Route
        path="/admin/delivery-pricing"
        element={<AdminLegacyRedirect to="/admin/configuration/delivery" />}
      />
      <Route
        path="/admin/deliveries"
        element={
          <AdminProtectedRoute path="/admin/deliveries">
            <AdminDeliveries />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <AdminProtectedRoute path="/admin/payments">
            <AdminPayments />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/subscription-management"
        element={<AdminLegacyRedirect to="/admin/configuration/monetization/plans-stripe" />}
      />
      <Route
        path="/admin/payment-config"
        element={<AdminLegacyRedirect to="/admin/configuration/monetization/accounts" />}
      />
      <Route
        path="/admin/settings"
        element={<AdminLegacyRedirect to="/admin/configuration/site" />}
      />
      <Route
        path="/admin/change-password"
        element={
          <AdminProtectedRoute path="/admin/change-password">
            <AdminChangePassword />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <AdminProtectedRoute path="/admin/clients">
            <AdminClients />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/search-analytics"
        element={
          <AdminProtectedRoute path="/admin/search-analytics">
            <AdminSearchAnalytics />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/medicine-stats"
        element={
          <AdminProtectedRoute path="/admin/medicine-stats">
            <AdminMedicinePublicHealthStats />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/subscription-stats"
        element={
          <AdminProtectedRoute path="/admin/subscription-stats">
            <AdminSubscriptionStats />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/subscription-pharmacies"
        element={
          <AdminProtectedRoute path="/admin/subscription-pharmacies">
            <AdminSubscriptionPharmacies />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/payment-history"
        element={
          <AdminProtectedRoute path="/admin/payment-history">
            <AdminPaymentHistory />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/admin-roles"
        element={<AdminLegacyRedirect to="/admin/configuration/access/roles" />}
      />
      <Route
        path="/admin/admin-users"
        element={<AdminLegacyRedirect to="/admin/configuration/access/users" />}
      />
      <Route
        path="/admin/kam-users"
        element={
          <AdminProtectedRoute path="/admin/kam-users">
            <AdminKamUsers />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/kam-commissions"
        element={
          <AdminProtectedRoute path="/admin/kam-commissions">
            <AdminKamCommissions />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/doctor-commission-config"
        element={<AdminLegacyRedirect to="/admin/configuration/commissions/doctor" />}
      />
      <Route
        path="/admin/configuration"
        element={
          <AdminProtectedRoute path="/admin/configuration">
            <AdminConfigurationIndex />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/site"
        element={
          <AdminProtectedRoute path="/admin/configuration/site">
            <AdminSettings />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/monetization/accounts"
        element={
          <AdminProtectedRoute path="/admin/configuration/monetization/accounts">
            <AdminPaymentConfig />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/monetization/plans-mm"
        element={
          <AdminProtectedRoute path="/admin/configuration/monetization/plans-mm">
            <AdminPlans />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/monetization/plans-stripe"
        element={
          <AdminProtectedRoute path="/admin/configuration/monetization/plans-stripe">
            <AdminSubscriptionManagement />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/commissions/kam"
        element={
          <AdminProtectedRoute path="/admin/configuration/commissions/kam">
            <AdminKamCommissionSettings />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/commissions/doctor"
        element={
          <AdminProtectedRoute path="/admin/configuration/commissions/doctor">
            <AdminDoctorCommissionConfig />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/delivery"
        element={
          <AdminProtectedRoute path="/admin/configuration/delivery">
            <AdminDeliveryPricing />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/access/users"
        element={
          <AdminProtectedRoute path="/admin/configuration/access/users">
            <AdminUsers />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration/access/roles"
        element={
          <AdminProtectedRoute path="/admin/configuration/access/roles">
            <AdminRoles />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/doctor-commissions"
        element={
          <AdminProtectedRoute path="/admin/doctor-commissions">
            <AdminDoctorCommissions />
          </AdminProtectedRoute>
        }
      />

      {/* Seller/POS Routes */}
      <Route
        path="/seller"
        element={
          <ProtectedRoute roles={["seller", "pharmacy"]}>
            <SellerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/pos"
        element={
          <ProtectedRoute roles={["seller", "pharmacy"]}>
            <SellerPOS />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/sales"
        element={
          <ProtectedRoute roles={["seller", "pharmacy"]}>
            <SellerSales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/invoices"
        element={
          <ProtectedRoute roles={["seller", "pharmacy"]}>
            <SellerInvoices />
          </ProtectedRoute>
        }
      />

      {/* Doctor */}
      <Route
        path="/doctor/prescriptions"
        element={
          <ProtectedRoute roles={["doctor"]}>
            <DoctorPrescriptionsPage />
          </ProtectedRoute>
        }
      />

      {/* Delivery */}
      <Route
        path="/delivery"
        element={
          <ProtectedRoute roles={["delivery", "admin"]}>
            <DeliveryDashboard />
          </ProtectedRoute>
        }
      />

      {/* KAM */}
      <Route
        path="/kam"
        element={
          <ProtectedRoute roles={["kam"]}>
            <KamDashboard />
          </ProtectedRoute>
        }
      />

      {/* Messages & Purchases Routes */}
      <Route
        path="/messages"
        element={
          <ProtectedRoute roles={["client", "pharmacy", "doctor"]}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:conversationId"
        element={
          <ProtectedRoute roles={["client", "pharmacy", "doctor"]}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases"
        element={
          <ProtectedRoute roles={["client", "pharmacy"]}>
            <PurchasesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute roles={["client"]}>
            <ClientProfilePage />
          </ProtectedRoute>
        }
      />
    </Routes>
        </GPSGuard>
      } />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="App min-h-screen bg-slate-50">
            <AppRoutes />
            <PWAInstallPrompt />
            <Toaster 
              position="top-center" 
              richColors 
              closeButton
              toastOptions={{
                style: {
                  fontFamily: 'Inter, sans-serif'
                }
              }}
            />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
